import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const TenantDetails = () => {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenantDetails = async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: 'https://your-api-endpoint',
          scope: 'openid email profile admin:read:users:all users:read delete:tenants',
        });

        const tenantResponse = await axios.get(`https://api.sevis.click/admin/tenant`, {
          params: { tenantId },
          headers: { Authorization: `Bearer ${token}` },
        });

        const tenantData = tenantResponse.data.tenant;
        setTenant(tenantData);

        if (tenantData?.customerSubdomain) {
          const usersResponse = await axios.get(`https://api.sevis.click/admin/users`, {
            params: { subdomain: tenantData.customerSubdomain },
            headers: { Authorization: `Bearer ${token}` },
          });
          setUsers(usersResponse.data);
        }
      } catch (error) {
        console.error('Error fetching tenant details or users:', error);
        setError('Failed to load tenant data.');
      } finally {
        setLoading(false);
      }
    };

    fetchTenantDetails();
  }, [tenantId, getAccessTokenSilently]);

  const handleDelete = async () => {
    if (!window.confirm("⚠️ Are you sure you want to delete this tenant and all associated data? This cannot be undone.")) return;

    try {
      setDeleting(true);
      const token = await getAccessTokenSilently({
        audience: 'https://your-api-endpoint',
        scope: 'delete:tenants'
      });

      await axios.delete(`https://api.sevis.click/admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Tenant deleted successfully.');
      navigate('/admin'); // Redirect to main admin page
    } catch (err) {
      console.error('Error deleting tenant:', err);
      alert('❌ Failed to delete tenant. See console for details.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2>Tenant Details</h2>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={styles.error}>{error}</p>
      ) : tenant ? (
        <>
          <div style={styles.section}>
            <p><strong>Tenant ID:</strong> {tenant.tenantId}</p>
            <p><strong>Customer Name:</strong> {tenant.customerName}</p>
            <p><strong>Customer Subdomain:</strong> {tenant.customerSubdomain}</p>
            <p><strong>Product:</strong> {tenant.product || 'N/A'}</p>
            <p><strong>Created At:</strong> {tenant.createdAt}</p>
            <p><strong>Modified At:</strong> {tenant.modifiedAt}</p>
            <p><strong>Expiry Time:</strong> {tenant.expiryTime || 'N/A'}</p>
          </div>

          <h3>Users in this Tenant</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>User ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Job Title</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId}>
                  <td>{user.userId}</td>
                  <td>{user.name}</td>
                  <td>{user.email || 'N/A'}</td>
                  <td>{user.jobTitle || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={styles.buttonContainer}>
            <button onClick={handleDelete} disabled={deleting} style={styles.dangerButton}>
              {deleting ? 'Deleting...' : 'Delete Tenant'}
            </button>
          </div>
        </>
      ) : (
        <p>Tenant not found.</p>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem'
  },
  section: {
    marginBottom: '2rem',
    padding: '1rem',
    border: '1px solid #ccc',
    borderRadius: '8px',
    backgroundColor: '#f9f9f9'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
    marginBottom: '2rem'
  },
  dangerButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  buttonContainer: {
    textAlign: 'right'
  },
  error: {
    color: 'red'
  }
};

export default TenantDetails;
